import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "@/components/wam/Nav";
import Grain from "@/components/wam/Grain";
import Cursor from "@/components/wam/Cursor";
import Footer from "@/components/wam/Footer";
import Corners from "@/components/wam/Corners";

const GHL_CAL_ID = "WGbRNaPia1Cil2uRYo8Q";
const GHL_BASE = `https://link.smallbusinessautomator.com/widget/booking/${GHL_CAL_ID}`;
const GHL_SCRIPT = "https://link.smallbusinessautomator.com/js/form_embed.js";

export default function Book() {
  const [params] = useSearchParams();
  const [iframeReady, setIframeReady] = useState(false);
  const iframeRef = useRef(null);

  const src = useMemo(() => {
    const q = new URLSearchParams();
    const first = params.get("first_name") || params.get("first") || "";
    const last = params.get("last_name") || params.get("last") || "";
    const email = params.get("email") || "";
    const phone = params.get("phone") || "";
    if (first) q.set("first_name", first);
    if (last) q.set("last_name", last);
    if (email) q.set("email", email);
    if (phone) q.set("phone", phone);
    const qs = q.toString();
    return qs ? `${GHL_BASE}?${qs}` : GHL_BASE;
  }, [params]);

  // Load GHL's auto-resize script once
  useEffect(() => {
    if (document.querySelector(`script[src="${GHL_SCRIPT}"]`)) return;
    const s = document.createElement("script");
    s.src = GHL_SCRIPT;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <div className="relative bg-[#070709] text-[#F0ECE4] min-h-screen overflow-x-hidden">
      <Grain />
      <Cursor />
      <Nav />

      {/* Ambient gold radial glow */}
      <div
        aria-hidden
        className="absolute left-1/2 top-[15%] -translate-x-1/2 h-[600px] w-[900px] pointer-events-none opacity-[0.35]"
        style={{
          background:
            "radial-gradient(closest-side, rgba(201,168,76,0.18), transparent 70%)",
        }}
      />

      <main className="relative pt-40 pb-24 md:pt-48 md:pb-32">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8 flex items-center justify-center gap-4"
          >
            <Link
              to="/"
              className="font-mono text-[10px] tracking-[0.3em] text-[#F0ECE4]/40 hover:text-[#C9A84C] transition-colors"
              data-testid="book-back-link"
            >
              ← BACK TO WAM
            </Link>
            <span className="font-mono text-[10px] tracking-[0.3em] text-[#F0ECE4]/25">
              /
            </span>
            <span className="font-mono text-[10px] tracking-[0.3em] text-[#C9A84C]">
              STEP 02 · BOOK YOUR CALL
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-center mx-auto max-w-[900px]"
            style={{
              fontSize: "clamp(36px, 6vw, 84px)",
              lineHeight: 0.95,
              letterSpacing: "-0.035em",
            }}
          >
            One 15-minute call.
            <br />
            <span className="text-[#C9A84C]">We&rsquo;ll tell you honestly if WAM fits.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.55 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mx-auto mt-8 max-w-[620px] text-center font-body text-[15px] leading-[1.75] text-[#F0ECE4]"
          >
            Pick a time that works. We&rsquo;ll confirm your niche, walk through the
            system live, and tell you whether WAM is the right move for your
            pipeline — no pitch deck, no pressure.
          </motion.p>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 font-mono text-[10px] tracking-[0.28em] text-[#F0ECE4]/45"
          >
            <span>15 MINUTES</span>
            <span className="text-[#C9A84C]/40">·</span>
            <span>ZERO PITCH DECK</span>
            <span className="text-[#C9A84C]/40">·</span>
            <span>WITH DALLAS DIRECTLY</span>
          </motion.div>

          {/* Calendar frame */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative mt-14 md:mt-20 mx-auto max-w-[1080px]"
            data-testid="book-calendar-frame"
          >
            <div className="relative border border-[#C9A84C]/25 bg-[#0b0b0e] p-3 md:p-4 shadow-[0_60px_180px_-40px_rgba(0,0,0,0.9)]">
              <Corners />
              <div className="relative bg-white">
                {!iframeReady && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#0b0b0e] z-10">
                    <div className="font-mono text-[11px] tracking-[0.3em] text-[#C9A84C]/70 animate-pulse">
                      LOADING CALENDAR&hellip;
                    </div>
                  </div>
                )}
                <iframe
                  ref={iframeRef}
                  title="Book your WAM discovery call"
                  src={src}
                  id={`${GHL_CAL_ID}_wam`}
                  style={{
                    width: "100%",
                    border: "none",
                    overflow: "hidden",
                    display: "block",
                    minHeight: "760px",
                  }}
                  scrolling="no"
                  onLoad={() => setIframeReady(true)}
                  data-testid="ghl-calendar-iframe"
                />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2 font-mono text-[9px] tracking-[0.28em] text-[#F0ECE4]/35">
              <span>WAM · DISCOVERY CALL</span>
              <span>SBA / 2025</span>
            </div>
          </motion.div>

          {/* Fallback link */}
          <div className="mt-10 text-center">
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[10px] tracking-[0.28em] text-[#F0ECE4]/40 hover:text-[#C9A84C] transition-colors"
              data-testid="book-fallback-link"
            >
              Calendar not loading? Open in new tab →
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
